import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import {
  WHATSAPP_TEMPLATE_BODY_MAX,
  WHATSAPP_TEMPLATE_KEYS,
  type PrepareWhatsAppBody,
  type SendWhatsAppBody,
  type SendWhatsAppResponse,
  type WhatsAppTemplateKeyCode,
} from "@gioia365/contracts";
import { DAILY_RECIPIENT_CAP, renderTemplate, templateParametersOf, type WhatsAppFallbackReason } from "@gioia365/domain/whatsapp";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "../design/components/Button";
import { Dialog } from "../design/components/Dialog";
import { Icon } from "../design/components/Icon";
import { Reason } from "../design/components/Reason";
import "../design/components/text-field.css";
import { useToast } from "../design/components/Toast";
import { messageForError } from "../i18n/errors";
import { useT, type Translate } from "../i18n/locale";
import "../leads/card/call-desk.css";
import { formatPhoneForDisplay } from "../leads/lead-display";
import { leadCardQuery, useLeadView } from "../leads/lead-queries";
import { useAccess } from "../session/access";
import type { TabContext } from "../session/tab-context";
import {
  alreadyDeclared,
  preparedMessageFor,
  sendRequestIdFor,
  useDeclareWhatsAppSent,
  usePrepareWhatsApp,
  useSendWhatsApp,
  whatsAppTemplatesQuery,
  type PreparedMessage,
} from "./api";
import { composeMessage, dailyCapWarningOf, recipientsOf, templateValuesOf, whatsAppLinkOf, whatsAppRouteOf, type Recipient } from "./message";
import { dailyCapRefusalOf, dailyCapText, fallbackReasonText, missingFields, prepareRefusalText, unavailableReasonText } from "./whatsapp-texts";
import "./whatsapp.css";

// La finestra del WhatsApp a mano (piano M6, R1, Task 4a; D4, D5, D25; ruling E7 ed E8; mock-up `scheda-lead.html`, `#m-whatsapp`,
// e `mockup.js`). Il primo passo: «A chi scrivi» se il Lead ha due cellulari che ricevono, il modello dell'azienda, l'anteprima
// del testo che il Lead riceverà (con la riga «scrivi STOP» dell'azienda in fondo) e il testo da ritoccare; una variabile senza
// valore resta fra graffe e la finestra chiede di completarla nel testo (D5). Tutto si calcola nel browser: i modelli arrivano con
// la scheda, e l'apertura non aspetta il server. «Apri WhatsApp» è un link vero verso `wa.me` in una scheda nuova (`noopener`), e
// il suo clic registra il messaggio preparato con il testo esatto del link, senza fermare l'apertura (niente blocco dei popup); la
// domanda prende il posto del messaggio subito dopo il clic, quando il browser ha già aperto la scheda (il link resta nella pagina
// fino ad allora).
// Il secondo passo: «Hai inviato il messaggio?», con «Sì, inviato» (la dichiarazione) e «No, non l'ho inviato» (resta
// «WhatsApp preparato», D4); «Riapri WhatsApp» riapre lo stesso messaggio, e così una nuova apertura della finestra con lo stesso
// cellulare e lo stesso testo (ruling E8). Il link che il server rimanda è il controllo: se è diverso da quello aperto (la riga
// dell'azienda cambiata nel frattempo), la finestra lo dice e «Riapri WhatsApp» apre quello registrato. Un cellulare che non è un
// numero internazionale è un cellulare non valido, detto come motivo. Dalla postazione (Task 4b) la finestra porta la chiamata
// (`callId`) e il modello proposto dopo l'esito.
//
// Il WhatsApp ufficiale (piano M6, R2, Task 9b; D2, D3, D19; ruling A4c, A4f, E4; questioni 413 e 414; mock-up `mockup.js`,
// `waRoute` e `syncWaMode`): la finestra calcola la scelta prima del clic con `whatsappRoute` del dominio, dagli stessi fatti del
// server (`whatsAppRouteOf`: il WhatsApp di chi scrive dalla scheda, l'approvazione del modello, i valori completi). Con la scelta
// `send` del WhatsApp ufficiale il pulsante è «Invia il WhatsApp», il testo è soltanto anteprima (il modello approvato con i valori
// puliti, come lo riceve il Lead: non si ritocca) e «Apri WhatsApp» resta come seconda scelta a mano; altrimenti la finestra è quella
// a mano, con il perché del ripiego (le frasi di `waRoute`). L'avviso del tetto si vede prima del clic, anche a mano, dal conteggio
// della scheda (una conferma non avvisa). L'invio porta un identificativo della richiesta per Lead, modello e cellulare, tenuto nella
// cache della pagina finché il server non risponde con il suo esito (`sendRequestIdFor`, E4): un nuovo clic dopo una rete caduta,
// anche dopo aver chiuso e riaperto la finestra, ritrova lo stesso messaggio invece di mandarne un secondo. Il pulsante resta spento
// finché il server non risponde, e intanto la finestra non si chiude (Esc, il fondo, «Chiudi» e «Annulla»): chi aspetta vede come è
// andata. Il server ricontrolla:
// - preso in carico da Meta: la finestra si chiude e la Timeline lo mostra («Preso in carico da Meta», Task 10a); se il server avvisa
//   del tetto e la finestra non l'aveva detto (il conteggio della scheda era vecchio), l'avviso dopo l'invio lo ripete (413);
// - rifiutato da Meta, oppure tenuto a mano (il ripiego del server, o il WhatsApp della persona a mano): niente si riprova da sé; la
//   finestra lo dice e passa a mano per quel modello e quel cellulare, con «Apri WhatsApp» per un secondo clic (il browser bloccherebbe
//   una scheda aperta da sola dopo un'attesa);
// - un rifiuto (`409 whatsapp_recipient_not_allowed` fuori dalla produzione: «Numero non ammesso nell'azienda di prova…», un Lead
//   bloccato, la rete): il motivo, e «Apri WhatsApp» resta.
//
// WhatsApp Agile (piano M6, R9, Task 27b; WA-5, WA-6, WA-7, WA-10, WA-12, INFRA-8; mock-up `mockup.js`, `syncWaMode` e la postazione):
// con il WhatsApp Agile di chi scrive pronto la finestra è come quella del WhatsApp ufficiale («Invia il WhatsApp», «Apri WhatsApp» come
// seconda scelta, la nota «Parte da solo con WhatsApp Agile dal tuo numero …»), ma il testo si ritocca (WA-6) come a mano: l'anteprima
// tiene la riga «scrivi STOP» in fondo e parte quel testo (`editedText`, che il server riempie di nuovo senza rimettere la riga). Un
// campo che resta vuoto si completa nel testo prima di inviare. Prima del clic il tetto dei 2 al giorno ferma Agile (WA-10, dal
// conteggio della scheda; le conferme passano) e il canale salvato giù da meno di 10 minuti tiene a mano (`sendVia.ready`). All'invio
// il server ricontrolla e ogni ripiego si dice ad alta voce, con le parole di ogni motivo: il canale giù, il fornitore che non risponde,
// il telefono non collegato, il numero non ammesso, il ritmo con l'ora del prossimo invio, il tetto (`409 whatsapp_daily_cap`, con il
// conteggio di oggi se il server l'ha letto); preso dal fornitore, «Preso in carico da WhatsApp Agile», mai «consegnato» (WA-7); non
// preso o senza risposta (può essere partito), la finestra lo dice e manda alla Timeline prima di riscriverlo a mano.

type Props = {
  tab: TabContext;
  leadId: string;
  /** La chiamata da cui nasce il messaggio («Invia WhatsApp» dopo l'esito). */
  callId?: string;
  /** Il modello da cui partire; senza, il primo dell'azienda. */
  suggestedTemplate?: WhatsAppTemplateKeyCode;
  /** Il messaggio è preparato (anche se poi la finestra si chiude senza risposta), oppure preso in carico da Meta. */
  onPrepared?: (message: { messageId: string; templateKey: WhatsAppTemplateKeyCode }) => void;
  /** Chi scrive ha risposto a «Hai inviato il messaggio?». */
  onDone?: (result: { messageId: string; templateKey: WhatsAppTemplateKeyCode; declaredSent: boolean }) => void;
  onClose: () => void;
};

type Phase =
  | { kind: "compose" }
  | {
      kind: "confirm";
      templateKey: WhatsAppTemplateKeyCode;
      /** Il link aperto dal browser. */
      link: string;
      /** Il messaggio registrato; `null` finché la preparazione non risponde. */
      message: PreparedMessage | null;
    };

/** Il titolo: «WhatsApp a Giulia», con due cellulari «WhatsApp a Giulia o Marco». */
function titleOf(t: Translate, recipients: readonly Recipient[]): string {
  const names = recipients.map((recipient) => recipient.firstName ?? recipient.name?.split(" ")[0] ?? formatPhoneForDisplay(recipient.e164));
  const [first, second] = names;
  if (first === undefined) return t("whatsapp.button");
  return t("whatsapp.dialog.title", { names: second === undefined ? first : t("whatsapp.dialog.twoNames", { first, second }) });
}

/** Una voce di «A chi scrivi»: «Giulia Ferri · +39 300 000 0142», senza nome il posto del cellulare. */
function recipientText(t: Translate, recipient: Recipient): string {
  const name = recipient.name ?? t(recipient.holder === "second" ? "leadCard.fields.secondHolder" : "leadCard.fields.mobile");
  return t("whatsapp.dialog.recipient", { name, number: formatPhoneForDisplay(recipient.e164) });
}

/**
 * Perché il server ha tenuto a mano un messaggio: il suo ripiego (con il motivo, il cui testo si compone a ogni disegno con i modelli
 * riletti, e con il ritmo di Agile l'ora del prossimo invio), il WhatsApp della persona a mano, il rifiuto del fornitore (Meta o
 * WhatsApp Agile, che può anche non aver risposto), oppure il tetto di Agile (`409 whatsapp_daily_cap`, con il conteggio di oggi).
 */
type HeldManual =
  | { kind: "fallback"; reason: WhatsAppFallbackReason; nextAt: string | null }
  | { kind: "manual" }
  | { kind: "refused"; channel: "meta" | "agile" }
  | { kind: "cap"; count: number | null };

/** I WhatsApp che partono da soli dalla finestra: il WhatsApp ufficiale e WhatsApp Agile. */
type AutomaticChannel = "meta" | "agile";

/** La nota sotto il messaggio: da quale numero parte da solo, o che il pulsante apre WhatsApp Desktop. */
function noteOf(t: Translate, automatic: AutomaticChannel | null, sendVia: { e164: string } | null): string {
  if (automatic === null || sendVia === null) return t("whatsapp.dialog.manualNote");
  const number = formatPhoneForDisplay(sendVia.e164);
  return automatic === "agile" ? t("whatsapp.dialog.agileNote", { number }) : t("whatsapp.dialog.officialNote", { number });
}

/** Gli identificativi da mettere in `aria-describedby`, oppure niente. */
function describedBy(...ids: (string | null)[]): string | undefined {
  const present = ids.filter((id): id is string => id !== null);
  return present.length > 0 ? present.join(" ") : undefined;
}

export function WhatsAppDialog({ tab, leadId, callId, suggestedTemplate, onPrepared, onDone, onClose }: Props) {
  const t = useT();
  const toast = useToast();
  const access = useAccess();
  const view = useLeadView();
  const queryClient = useQueryClient();
  const ids = useId();
  const card = useQuery(leadCardQuery(tab, leadId, view)).data;
  const templates = useQuery(whatsAppTemplatesQuery(tab));
  const prepare = usePrepareWhatsApp(tab, leadId, view);
  const declare = useDeclareWhatsAppSent(tab, view);
  const send = useSendWhatsApp(tab, leadId, view);
  const [templateKey, setTemplateKey] = useState<WhatsAppTemplateKeyCode>(suggestedTemplate ?? WHATSAPP_TEMPLATE_KEYS[0]);
  const [phoneId, setPhoneId] = useState<string | null>(null);
  // Il testo ritoccato; `null` finché è il modello riempito, che riparte quando cambiano modello o cellulare ({nome} cambia).
  const [draft, setDraft] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>({ kind: "compose" });
  const [error, setError] = useState<string | null>(null);
  // Modello e cellulare che il server ha tenuto a mano (il suo ripiego, il WhatsApp della persona a mano o il rifiuto di Meta), con il
  // perché: finché la finestra è aperta quel messaggio si manda a mano.
  const [heldManual, setHeldManual] = useState<ReadonlyMap<string, HeldManual>>(() => new Map());
  // Dopo una risposta che tiene il messaggio a mano, il focus va su «Apri WhatsApp» (o sul testo da completare).
  const focusManual = useRef(false);
  const sentRef = useRef<HTMLButtonElement>(null);
  const openRef = useRef<HTMLAnchorElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);

  const ready = phase.kind === "confirm" && phase.message !== null;
  // Registrato il messaggio, il focus va su «Sì, inviato», come nel mock-up.
  useEffect(() => {
    if (ready) sentRef.current?.focus();
  }, [ready]);

  const recipients = card ? recipientsOf(card) : [];
  const recipient = recipients.find((candidate) => candidate.phoneId === phoneId) ?? recipients[0] ?? null;
  const data = templates.data;
  const template = data?.templates.find((candidate) => candidate.key === templateKey) ?? data?.templates[0] ?? null;
  const values = card ? templateValuesOf(card, recipient, access.context) : {};
  const sendVia = card?.whatsapp.sendVia ?? null;
  const todayCount = card?.whatsapp.todayCount ?? 0;
  const timeZone = access.context.settings.timeZone;
  const parameters = template ? templateParametersOf(template.body, values) : null;
  const requestKey = template && recipient ? `${template.key}|${recipient.phoneId}` : null;
  const held = requestKey === null ? undefined : heldManual.get(requestKey);
  // Con Agile il testo si ritocca: un campo vuoto lo dice la finestra (`problem`), non la scelta.
  const complete = sendVia?.channel === "agile" || (parameters !== null && parameters.missing.length === 0);
  const route = template && parameters ? whatsAppRouteOf(sendVia, template, complete, todayCount) : null;
  const automatic: AutomaticChannel | null = held === undefined && route?.kind === "send" && (route.channel === "meta" || route.channel === "agile") ? route.channel : null;
  const official = automatic === "meta";
  const writer = sendVia?.channel ?? null;
  // Il perché del ripiego, quando lo dice la scelta del browser (quello del server sta in `held`).
  const why = held === undefined && route?.kind === "fallback_manual" ? fallbackReasonText(t, route.reason, { channel: writer, template }) : null;
  let heldText: string | null = null;
  if (held?.kind === "refused") heldText = held.channel === "agile" ? t("whatsapp.dialog.agileRefused") : t("whatsapp.dialog.metaRefused");
  else if (held?.kind === "cap") heldText = dailyCapText(t, held.count);
  else if (held !== undefined) {
    heldText =
      (held.kind === "fallback" ? fallbackReasonText(t, held.reason, { channel: writer, template, nextAt: held.nextAt, timeZone }) : null) ??
      (writer === "agile" ? t("whatsapp.dialog.fallback.senderNotReadyAgile") : t("whatsapp.dialog.fallback.notReady"));
  }
  // Con il WhatsApp ufficiale il testo è il modello approvato con i valori puliti come li riceve Meta, e non si ritocca (ruling A4f);
  // con Agile e a mano si ritocca.
  const text = (official ? null : draft) ?? (template ? renderTemplate(template.body, official && parameters ? parameters.values : values).text : "");
  const message = composeMessage(text, values, data?.stopLine ?? "");
  const link = recipient && !message.empty ? whatsAppLinkOf(recipient.e164, message.text) : null;

  useEffect(() => {
    if (held === undefined || !focusManual.current) return;
    focusManual.current = false;
    (openRef.current ?? textRef.current)?.focus();
  }, [held]);

  // Perché WhatsApp non si apre: prima il Lead e chi scrive (la scheda), poi il cellulare, poi il testo. Fermano anche il WhatsApp
  // ufficiale, dove il testo può essere soltanto troppo lungo (valori lunghi): Meta lo rifiuterebbe.
  let problem: string | null = null;
  if (card && !card.whatsapp.available) problem = card.whatsapp.reason ? unavailableReasonText(t, card, card.whatsapp.reason) : null;
  else if (card && recipient === null) problem = unavailableReasonText(t, card, "no_mobile");
  else if (recipient && whatsAppLinkOf(recipient.e164, "") === null) problem = t("whatsapp.reasons.invalid_number");
  else if (template && message.empty) problem = t("whatsapp.dialog.empty");
  else if (message.missing.length > 0) problem = t(automatic === "agile" ? "whatsapp.dialog.missingSend" : "whatsapp.dialog.missing", { fields: missingFields(message.missing) });
  else if (message.tooLong) problem = t("whatsapp.dialog.tooLong", { max: WHATSAPP_TEMPLATE_BODY_MAX });
  const sending = send.isPending;
  const available = card !== undefined && card.whatsapp.available;
  const openable = available && template !== null && link !== null && problem === null && !sending;
  const sendable = automatic !== null && available && recipient !== null && problem === null;
  // L'avviso del tetto (questione 413), prima del clic e anche a mano: soltanto quando il messaggio può partire. Quando il tetto ferma
  // Agile lo dice già il perché del ripiego (o il tetto detto dal server), una volta sola.
  const capStopsAgile = (why !== null && route?.kind === "fallback_manual" && route.reason === "daily_cap") || held?.kind === "cap";
  const capWarning = available && template !== null && !capStopsAgile && dailyCapWarningOf(todayCount, template);
  const problemId = `${ids}-problem`;
  const capId = `${ids}-cap`;
  const whyId = `${ids}-why`;
  const heldId = `${ids}-held`;
  const sendingId = `${ids}-sending`;

  /** Esc, il fondo, «Chiudi» e «Annulla»: durante l'invio no (vedi in cima al file). */
  function close() {
    if (!sending) onClose();
  }

  function choose(change: () => void) {
    setError(null);
    setDraft(null);
    change();
  }

  function openWhatsApp() {
    if (!openable || !card || !recipient || !template || !link) return;
    const chosen = template.key;
    const body: PrepareWhatsAppBody = { templateKey: chosen, phoneId: recipient.phoneId, editedText: message.text, ...(callId === undefined ? {} : { callId }) };
    setError(null);
    // Il link si apre da sé, nello stesso clic: la preparazione non lo aspetta e non lo ferma (ruling E7). Lo stesso cellulare con lo
    // stesso testo e la stessa chiamata riusa il messaggio già preparato, o la preparazione ancora in viaggio (ruling E8).
    const entry = preparedMessageFor(queryClient, { tab, leadId, callId: callId ?? null, phoneId: recipient.phoneId, text: message.text }, async () => {
      const response = await prepare.mutateAsync(body);
      return { messageId: response.messageId, link: response.link };
    });
    // La domanda arriva dopo il clic: il link resta nella pagina finché il browser non ha aperto la scheda nuova.
    const swap = window.setTimeout(() => setPhase({ kind: "confirm", templateKey: chosen, link, message: entry.current() }), 0);
    entry.promise.then(
      (prepared) => {
        setPhase((current) => (current.kind === "confirm" && current.message === null ? { ...current, message: prepared } : current));
        // Anche per un messaggio riusato: la postazione segna fatto il passo della chiamata (Task 4b).
        onPrepared?.({ messageId: prepared.messageId, templateKey: chosen });
      },
      (failure: unknown) => {
        window.clearTimeout(swap);
        setPhase({ kind: "compose" });
        setError(prepareRefusalText(t, card, failure));
      },
    );
  }

  /** «Invia il WhatsApp», dal WhatsApp ufficiale o da WhatsApp Agile: vedi in cima al file. */
  async function sendAutomatic() {
    if (!sendable || automatic === null || !card || !recipient || !template || requestKey === null || sending) return;
    const channel = automatic;
    const chosen = template.key;
    const key = requestKey;
    const clientRequestId = sendRequestIdFor(queryClient, { tab, leadId, templateKey: chosen, phoneId: recipient.phoneId });
    const body: SendWhatsAppBody = {
      templateKey: chosen,
      clientRequestId,
      phoneId: recipient.phoneId,
      ...(callId === undefined ? {} : { callId }),
      // Con Agile parte il testo della finestra, ritoccato, con la riga «scrivi STOP» in fondo (WA-6, WA-12).
      ...(channel === "agile" ? { editedText: message.text } : {}),
    };
    setError(null);
    let response: SendWhatsAppResponse;
    try {
      response = await send.mutateAsync(body);
    } catch (failure) {
      // Il tetto di Agile (WA-10): la finestra propone «Apri WhatsApp» a mano, con il perché.
      const cap = dailyCapRefusalOf(failure);
      if (cap !== null) {
        focusManual.current = true;
        setHeldManual((current) => new Map(current).set(key, { kind: "cap", count: cap.count }));
        return;
      }
      setError(prepareRefusalText(t, card, failure));
      return;
    }
    if (response.messageId !== null && response.status !== "failed") {
      // «Preso in carico» soltanto quando il fornitore l'ha preso; la stessa richiesta che torna mentre aspetta ancora è «in invio».
      if (channel === "agile" && response.status === "sent") toast.show(t("whatsapp.dialog.sentToAgile"), "success");
      else if (channel === "agile") toast.show(t("whatsapp.dialog.queuedAtAgile"));
      // L'avviso del tetto che la finestra non aveva detto (il conteggio della scheda era vecchio), ripetuto dal server (413).
      else if (response.dailyCapWarning && !capWarning) toast.show(t("whatsapp.dialog.sentToMetaCap", { cap: DAILY_RECIPIENT_CAP }), "warning");
      else toast.show(t("whatsapp.dialog.sentToMeta"), "success");
      onPrepared?.({ messageId: response.messageId, templateKey: chosen });
      onClose();
      return;
    }
    // Rifiutato dal fornitore (nessun nuovo tentativo da sé), oppure tenuto a mano dal server, con il suo perché.
    let answer: HeldManual;
    if (response.messageId !== null) answer = { kind: "refused", channel };
    else if (response.route.kind === "fallback_manual") answer = { kind: "fallback", reason: response.route.reason, nextAt: response.route.nextAt ?? null };
    else answer = { kind: "manual" };
    focusManual.current = true;
    setHeldManual((current) => new Map(current).set(key, answer));
  }

  async function declareSent() {
    if (phase.kind !== "confirm" || phase.message === null || declare.isPending) return;
    const { messageId } = phase.message;
    setError(null);
    try {
      await declare.mutateAsync(messageId);
    } catch (failure) {
      // Già dichiarato (da un'altra scheda, dalla Timeline): è quello che si voleva.
      if (!alreadyDeclared(failure)) {
        setError(messageForError(t, failure));
        return;
      }
    }
    toast.show(t("whatsapp.dialog.sentDone"), "success");
    onDone?.({ messageId, templateKey: phase.templateKey, declaredSent: true });
    onClose();
  }

  function notSent() {
    if (phase.kind !== "confirm" || phase.message === null) return;
    toast.show(t("whatsapp.dialog.notSentDone"));
    onDone?.({ messageId: phase.message.messageId, templateKey: phase.templateKey, declaredSent: false });
    onClose();
  }

  const alert = error !== null && error !== problem ? error : null;
  const alertBox = alert ? (
    <Reason tone="warning" role="alert">
      {alert}
    </Reason>
  ) : null;

  // Una finestra sola per i due passi: la domanda prende il posto del messaggio, senza chiudere e riaprire la finestra.
  if (phase.kind === "confirm") {
    // Il link registrato dal server, se è arrivato: è quello del messaggio che si dichiara.
    const reopen = phase.message?.link ?? phase.link;
    return (
      <Dialog
        open
        size="wide"
        title={titleOf(t, recipients)}
        onClose={onClose}
        footer={
          <>
            <Button onClick={notSent} disabled={phase.message === null}>
              {t("whatsapp.dialog.notSent")}
            </Button>
            <Button ref={sentRef} variant="primary" onClick={() => void declareSent()} disabled={phase.message === null || declare.isPending} aria-busy={declare.isPending ? "true" : undefined}>
              {t("whatsapp.dialog.sent")}
            </Button>
          </>
        }
      >
        <ConfirmStep
          preparing={phase.message === null}
          otherText={phase.message !== null && phase.message.link !== phase.link}
          reopen={reopen}
          alert={alertBox}
        />
      </Dialog>
    );
  }

  // «Apri WhatsApp»: il pulsante principale a mano, la seconda scelta accanto a «Invia il WhatsApp».
  const openDescribedBy = describedBy(heldText ? heldId : null, problem ? problemId : null, capWarning ? capId : null, why ? whyId : null);
  const openControl = openable ? (
    <a
      ref={openRef}
      className="g-button"
      data-variant={automatic !== null ? "secondary" : "primary"}
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      aria-describedby={openDescribedBy}
      onClick={openWhatsApp}
      // Il clic con la rotella apre anche lui una scheda nuova.
      onAuxClick={(event) => {
        if (event.button === 1) openWhatsApp();
      }}
    >
      <Icon name="whatsapp" />
      {t("whatsapp.dialog.open")}
    </a>
  ) : (
    <Button variant={automatic !== null ? "secondary" : "primary"} disabled aria-describedby={openDescribedBy}>
      <Icon name="whatsapp" />
      {t("whatsapp.dialog.open")}
    </Button>
  );

  return (
    <Dialog
      open
      size="wide"
      title={titleOf(t, recipients)}
      onClose={close}
      footer={
        <>
          <Button onClick={close} disabled={sending} aria-describedby={sending ? sendingId : undefined}>
            {t("common.cancel")}
          </Button>
          {openControl}
          {automatic !== null ? (
            <Button
              variant="primary"
              disabled={!sendable || sending}
              aria-busy={sending ? "true" : undefined}
              aria-describedby={describedBy(problem ? problemId : null, capWarning ? capId : null)}
              onClick={() => void sendAutomatic()}
            >
              <Icon name="whatsapp" />
              {t("whatsapp.dialog.send")}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="g-stack g-whatsapp">
        {heldText ? (
          <Reason tone="warning" role="alert" id={heldId}>
            {heldText}
          </Reason>
        ) : null}
        {alertBox}
        {recipients.length > 1 ? (
          <fieldset className="g-desk__fieldset">
            <legend>{t("whatsapp.dialog.to")}</legend>
            <div className="g-desk__choices">
              {recipients.map((candidate) => (
                <label key={candidate.phoneId} className="g-desk__choice">
                  <input
                    type="radio"
                    name={`${ids}-to`}
                    value={candidate.phoneId}
                    checked={candidate.phoneId === recipient?.phoneId}
                    disabled={sending}
                    onChange={() => choose(() => setPhoneId(candidate.phoneId))}
                  />
                  <span>{recipientText(t, candidate)}</span>
                </label>
              ))}
            </div>
          </fieldset>
        ) : null}
        {templates.isPending || !card ? <p className="g-field__hint">{t("common.loading")}</p> : null}
        {templates.isError ? (
          <div className="g-whatsapp__row">
            <Reason tone="warning" role="alert">
              {messageForError(t, templates.error)}
            </Reason>
            <Button disabled={templates.isFetching} onClick={() => void templates.refetch()}>
              {t("common.retry")}
            </Button>
          </div>
        ) : null}
        {data && card ? (
          <>
            <fieldset className="g-desk__fieldset">
              <legend>{t("whatsapp.dialog.template")}</legend>
              <div className="g-desk__choices">
                {data.templates.map((candidate) => (
                  <label key={candidate.key} className="g-desk__choice">
                    <input
                      type="radio"
                      name={`${ids}-template`}
                      value={candidate.key}
                      checked={candidate.key === template?.key}
                      disabled={sending}
                      onChange={() => choose(() => setTemplateKey(candidate.key))}
                    />
                    <span>{candidate.name}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="g-field">
              <p id={`${ids}-preview`} className="g-field__label">
                {t("whatsapp.dialog.preview")}
              </p>
              <blockquote className="g-whatsapp__preview" aria-labelledby={`${ids}-preview`}>
                {message.text}
              </blockquote>
            </div>
            {official ? null : (
              <div className="g-field">
                <label className="g-field__label" htmlFor={`${ids}-text`}>
                  {t("whatsapp.dialog.text")}
                </label>
                <textarea
                  ref={textRef}
                  id={`${ids}-text`}
                  className="g-field__input g-whatsapp__text"
                  value={text}
                  aria-describedby={problem ? problemId : undefined}
                  // Mentre il testo parte da Agile non cambia: è quello che il Lead riceve.
                  readOnly={sending}
                  onChange={(event) => {
                    setError(null);
                    setDraft(event.target.value);
                  }}
                />
              </div>
            )}
          </>
        ) : null}
        {problem ? (
          <Reason tone="warning" role="alert" id={problemId}>
            {problem}
          </Reason>
        ) : null}
        {capWarning ? (
          <Reason tone="warning" id={capId}>
            {t("whatsapp.dialog.dailyCap", { count: todayCount, cap: DAILY_RECIPIENT_CAP })}
          </Reason>
        ) : null}
        {why ? <Reason id={whyId}>{why}</Reason> : null}
        {sending ? (
          <p className="g-field__hint" role="status" id={sendingId}>
            {automatic === "agile" ? t("whatsapp.dialog.sendingAgile") : t("whatsapp.dialog.sending")}
          </p>
        ) : null}
        <p className="g-field__hint">{noteOf(t, automatic, sendVia)}</p>
      </div>
    </Dialog>
  );
}

/**
 * «Hai inviato il messaggio?»: la domanda, l'attesa della registrazione, il testo registrato diverso e «Riapri WhatsApp». La usa anche
 * la risposta a mano di «Conversazioni → WhatsApp» (`ReplyDialog.tsx`, piano M6, R3).
 */
export function ConfirmStep({ preparing, otherText, reopen, alert }: { preparing: boolean; otherText: boolean; reopen: string; alert: ReactNode }) {
  const t = useT();
  return (
    <div className="g-stack g-whatsapp">
      <p className="g-whatsapp__question">{t("whatsapp.dialog.sentQuestion")}</p>
      <p className="g-field__hint">{t("whatsapp.dialog.sentHint")}</p>
      {preparing ? (
        <p className="g-field__hint" role="status">
          {t("whatsapp.dialog.preparing")}
        </p>
      ) : null}
      {otherText ? <Reason tone="warning">{t("whatsapp.dialog.otherText")}</Reason> : null}
      {alert}
      <div>
        <a className="g-button" data-variant="ghost" href={reopen} target="_blank" rel="noopener noreferrer">
          <Icon name="whatsapp" />
          {t("whatsapp.dialog.reopen")}
        </a>
      </div>
    </div>
  );
}
